import React, {ReactNode, useEffect, useRef, useState} from 'react';

import {v4} from 'uuid';

import StateButton from 'app/general/widgets/StateButton';
import {
  useAccountSettingsState,
  useUpdateAccountSettings,
} from 'app/settings/api';
import {gt} from 'app/utils/i18n';

import {MessageExchange} from './TextEdition/MessagePanel';

import {useWsOpenaiMessage} from '../hooks';
import {AI_SUGGESTION_PROMPT, AI_SUGGESTION_PROMPT_DEFAULT} from '../utils';

import type {SuggestionItem} from '../types';

type Props = {
  setResults: (results: Array<SuggestionItem>) => void;
  children: ReactNode;
  contentType?: string;
  initialThreadId?: string;
  disabled?: boolean;
  shouldUpdateAccount?: boolean;
};
export const SuggestionChat = ({
  setResults,
  children,
  initialThreadId,
  shouldUpdateAccount = false,
  contentType = 'policies and procedures',
  disabled = false,
}: Props) => {
  const [threadId] = useState(initialThreadId || v4());

  const {account_id, industry_classification} = useAccountSettingsState().data!;

  const [suggestions, setSuggestions] = useState<Array<SuggestionItem>>([]);

  const {sendMessage, isLoading, commands, isError, isOpen} =
    useWsOpenaiMessage({
      type: 'document_suggestions',
      thread_id: threadId,
    });
  // The number of completed suggestions so far.
  // Stored in a ref since closures need to interact with it.
  const numCompletedSuggestions = useRef(0);

  useEffect(() => {
    if (suggestions.length > 0) {
      setResults(suggestions);
    }
  }, [suggestions]);

  const [category, setCategory] = useState(
    industry_classification || 'online shopping'
  );
  const [updateAccountSettings] = useUpdateAccountSettings();
  const createSuggestions = () => {
    if (shouldUpdateAccount) {
      updateAccountSettings({account_id, industry_classification: category});
    }
    const prompt =
      AI_SUGGESTION_PROMPT[contentType] || AI_SUGGESTION_PROMPT_DEFAULT;
    sendMessage(prompt.replace('{industry}', category));
  };

  useEffect(() => {
    if (!isLoading) {
      numCompletedSuggestions.current = suggestions.length;
    }
  }, [isLoading]);

  useEffect(() => {
    if (commands.length > 0) {
      const existing = suggestions
        .slice(0, numCompletedSuggestions.current)
        .map((suggestion) => suggestion.name);

      const addedSuggestions: Array<SuggestionItem> = [];
      commands.forEach((command) => {
        switch (command.command) {
          case 'actions':
            if (
              'name' in command &&
              'content_type' in command &&
              !existing.includes(command.name)
            ) {
              // suggestion type has name/content_type fields.
              addedSuggestions.push(command);
            }
            break;
        }
      });
      if (addedSuggestions.length > 0) {
        setSuggestions([
          ...suggestions.slice(0, numCompletedSuggestions.current),
          ...addedSuggestions,
        ]);
      }
    }
  }, [commands]);

  return (
    <>
      {!isLoading && suggestions.length === 0 ? (
        <div className="form-inline mt-4 mb-1">
          <label htmlFor="category" className="mr-2">
            {gt('Suggest %s for a business in', contentType)}
          </label>
          <input
            id="category"
            className="mr-2"
            type="text"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          />
          <StateButton
            type="submit"
            busyText={gt('Suggesting...')}
            className="btn btn btn-primary"
            disabled={disabled || isError || !isOpen}
            onClick={createSuggestions}
          >
            {gt('Suggest')}
          </StateButton>{' '}
        </div>
      ) : null}
      <div className="row">
        <div
          className={
            isLoading || suggestions.length > 0 ? 'col-12 col-md-8' : 'col-12'
          }
        >
          {children}
        </div>
        {isLoading || suggestions.length > 0 ? (
          <div className="col-12 col-md-4">
            <MessageExchange
              threadId={threadId}
              chatType={'document_suggestions'}
              disabled={disabled}
            />
          </div>
        ) : null}
      </div>
    </>
  );
};
